import type { Character, MoveRef } from '@/schemas/character';
import type { ClassDefinition } from '@/schemas/classDefinition';
import { hasMoveSetup, type Move } from '@/schemas/move';
import { findMoveByIdGlobal } from '@/data/moves';
import { getGrantedPool } from '@/services/moveGrants';
import { getBundleMovesToApply } from '@/services/bundle';
import { getMulticlassMovePool } from '@/services/multiclass';
import {
  areAllSpellGrantsSelectionsValid,
  getActiveSpellGrantsForLevelUpMany,
} from '@/services/spellGrants';
import { isMoveSetupComplete } from '../../services/moveSetupComplete';
import { buildJustAcquiredMoves } from './justAcquired';
import type { LevelUpDraft, LevelUpGrantedChoiceDraft, LevelUpMulticlassPickDraft } from './types';

/**
 * Pure functions per calcolare la completion check dei singoli step del
 * `LevelUpFlow`. Centralizzate qui per riuso da `LevelUpFlowPage`
 * (`<StepNav>` esterno) senza violare `react-refresh/only-export-components`
 * (le funzioni vivono in un file `.ts`, non `.tsx`).
 *
 * Logica preservata da pre-PR-9: i 4 step rifatti in PR 9 non avevano
 * più i bottoni interni con relativa `computeCanProceed`; quella logica è
 * stata sollevata qui per essere riusata dal layer pagina.
 */

export function isPastTenStepComplete(draft: LevelUpDraft): boolean {
  return draft.pastTen !== undefined;
}

/**
 * **FI-80 (2026-05-24)** — Gating dello step archetype con setup pendente
 * della mossa-archetipo del 2° archetipo (es. `mago.archetipo-umano` con
 * 18 spell choices). Lo step è completo SE:
 *  1. `secondaryArchetypeId` definito, AND
 *  2. Se la mossa-archetipo del 2° archetipo ha `hasMoveSetup`, allora
 *     `secondaryArchetypeSetup` è definito E `isMoveSetupComplete` ritorna `true`.
 */
export function isArchetypeStepComplete(
  draft: LevelUpDraft,
  character: Character,
  classDef: ClassDefinition,
): boolean {
  if (draft.secondaryArchetypeId === undefined) return false;
  const arch = classDef.archetypes.find((a) => a.id === draft.secondaryArchetypeId);
  if (arch === undefined) return false;
  const archetypeMove = findMoveByIdGlobal(arch.grantedMoveId);
  if (archetypeMove === undefined || !hasMoveSetup(archetypeMove)) return true;
  if (draft.secondaryArchetypeSetup === undefined) return false;
  const ref: MoveRef = { id: arch.grantedMoveId, ...draft.secondaryArchetypeSetup };
  return isMoveSetupComplete(archetypeMove, ref, character.archetypeId);
}

export function isSpellsStepComplete(draft: LevelUpDraft, character: Character): boolean {
  if (!draft.moveAdded) return false;
  const justAcquired = buildJustAcquiredMoves(
    character.classId,
    draft.moveAdded,
    draft.grantedChoice,
    draft.multiclassPick,
  );
  const activeGrants = getActiveSpellGrantsForLevelUpMany(
    character.classId,
    character.moves,
    justAcquired,
  );
  return areAllSpellGrantsSelectionsValid(activeGrants, draft.spellSelections);
}

/**
 * Confirm complete = draft applicabile via `applyLevelUp`. Riproduce
 * la logica di `checkDraftCompleteness` di `applyLevelUpHelpers.ts`:
 *  - pastTen='addio': sempre valido (Addio salta tutti gli step interim)
 *  - altri: moveAdded richiesto; statIncreased richiesto solo se non
 *    post-10° (Apprendista/Instancabile)
 */
export function isConfirmStepComplete(draft: LevelUpDraft, character: Character): boolean {
  if (draft.pastTen === 'addio') return true;
  if (!draft.moveAdded) return false;
  const isPostTen =
    draft.pastTen === 'apprendista' ||
    draft.pastTen === 'instancabile' ||
    character.pastTen === 'apprendista' ||
    character.pastTen === 'instancabile';
  if (!isPostTen && !draft.statIncreased) return false;
  return true;
}

export function isGrantedStepComplete(
  granted: LevelUpGrantedChoiceDraft | undefined,
  character: Character,
  classDef: ClassDefinition,
  moveDef: Move,
): boolean {
  const grant = moveDef.grantsMoveChoice;
  if (!grant) return false;
  if (granted === undefined) return false;
  const pool = getGrantedPool(character, grant, classDef);
  if (!pool.includes(granted.selectedOptionId)) return false;
  if (grant.kind === 'from-choice-move') return true;
  const move = findMoveByIdGlobal(granted.selectedOptionId);
  if (!move || !hasMoveSetup(move)) return true;
  const ref: MoveRef = granted.selectedMoveSetup
    ? { id: granted.selectedOptionId, ...granted.selectedMoveSetup }
    : { id: granted.selectedOptionId, selectedChoices: [] };
  return isMoveSetupComplete(move, ref, character.archetypeId);
}

export function isMulticlassStepComplete(
  pick: LevelUpMulticlassPickDraft | undefined,
  character: Character,
  classDef: ClassDefinition,
  moveDef: Move,
): boolean {
  const config = moveDef.multiclass;
  if (!config || !pick?.selectedMoveId) return false;
  const movePool = getMulticlassMovePool(character, pick.sourceClassId, config, classDef);
  const selectedMove = movePool.find((m) => m.id === pick.selectedMoveId);
  if (!selectedMove) return false;
  if (!isTargetSetupComplete(selectedMove, pick, character)) return false;
  if (!areBundleSetupsComplete(selectedMove, pick, character)) return false;
  return isGrantedOfTargetComplete(selectedMove, pick, character);
}

/**
 * **FI-84 enhanced (2026-05-24)** — Gating cascade granted-of-target.
 * Walka attraverso la `grantedChain` verificando ogni step:
 *  - Anchor del livello ha grantsMoveChoice.
 *  - Step.selectedMoveId è nel pool della grant.
 *  - Se mossa concessa ha setup, step.selectedSetup è popolato +
 *    `isMoveSetupComplete` ritorna true.
 *
 * Per `from-choice-mapping`: il primo step è auto-derivato dal mapping
 * — chain può ometterlo (gating considera questo caso valido).
 *
 * Per `from-list`: l'utente DEVE fornire chain[0]. Pool vuoto / mossa
 * non scelta → step incompleto.
 */
function isGrantedOfTargetComplete(
  selectedMove: Move,
  pick: LevelUpMulticlassPickDraft,
  character: Character,
): boolean {
  const grant = selectedMove.grantsMoveChoice;
  if (grant === undefined) return true;
  // Caso speciale from-choice-mapping: il primo step è auto-derivato.
  // Walking della chain parte virtualmente dal mapping → controlliamo
  // setup della mossa mappata se ha setup interattivo.
  if (grant.kind === 'from-choice-mapping') {
    return isCascadeCompleteForMappingTarget(selectedMove, pick, character);
  }
  // from-list: chain[0] richiesto.
  if (grant.kind === 'from-list') {
    return isCascadeCompleteForExplicitChain(selectedMove, pick, character);
  }
  // Altri kind (from-group / from-choice-move) non gestiti come
  // granted-of-target dal multiclass — assume complete.
  return true;
}

function isCascadeCompleteForMappingTarget(
  selectedMove: Move,
  pick: LevelUpMulticlassPickDraft,
  character: Character,
): boolean {
  const grant = selectedMove.grantsMoveChoice;
  if (grant?.kind !== 'from-choice-mapping') return true;
  const mappedId = resolveMappedIdFromTarget(grant, pick);
  if (mappedId === null) return false; // choice non selezionata
  if (mappedId === undefined) return true; // mapping miss → ok
  const chain = pick.grantedChain ?? [];
  if (chain.length === 0) return isMappedMoveOptional(mappedId);
  return validateChainSteps(selectedMove, chain, character);
}

function resolveMappedIdFromTarget(
  grant: { kind: 'from-choice-mapping'; mapping: Record<string, string> },
  pick: LevelUpMulticlassPickDraft,
): string | null | undefined {
  const choice = pick.selectedMoveSetup?.selectedChoices?.[0];
  if (choice === undefined) return null;
  return grant.mapping[choice];
}

function isMappedMoveOptional(mappedId: string): boolean {
  const mappedMove = findMoveByIdGlobal(mappedId);
  if (mappedMove === undefined) return true;
  return !hasMoveSetup(mappedMove);
}

function isCascadeCompleteForExplicitChain(
  selectedMove: Move,
  pick: LevelUpMulticlassPickDraft,
  character: Character,
): boolean {
  const chain = pick.grantedChain ?? [];
  if (chain.length === 0) return false; // from-list richiede scelta
  return validateChainSteps(selectedMove, chain, character);
}

/**
 * Walks attraverso la chain, verificando ogni step. Anchor iniziale
 * è `selectedMove` (target multiclass). Ad ogni passo, anchor diventa
 * la mossa dello step precedente.
 */
function validateChainSteps(
  selectedMove: Move,
  chain: NonNullable<LevelUpMulticlassPickDraft['grantedChain']>,
  character: Character,
): boolean {
  let currentAnchor: Move | undefined = selectedMove;
  for (const step of chain) {
    if (currentAnchor === undefined) return false;
    const grant = currentAnchor.grantsMoveChoice;
    if (grant === undefined) return false;
    if (!isSelectedMoveIdValidForGrant(grant, step.selectedMoveId)) return false;
    const grantedMove = findMoveByIdGlobal(step.selectedMoveId);
    if (grantedMove === undefined) return false;
    if (hasMoveSetup(grantedMove)) {
      if (step.selectedSetup === undefined) return false;
      const ref: MoveRef = { id: step.selectedMoveId, ...step.selectedSetup };
      if (!isMoveSetupComplete(grantedMove, ref, character.archetypeId)) return false;
    } else if (step.selectedSetup !== undefined) {
      return false; // mossa senza setup ma setup fornito = incoerenza
    }
    currentAnchor = grantedMove;
  }
  return true;
}

function isSelectedMoveIdValidForGrant(
  grant: NonNullable<Move['grantsMoveChoice']>,
  selectedMoveId: string,
): boolean {
  if (grant.kind === 'from-list') return grant.moveOptions.includes(selectedMoveId);
  if (grant.kind === 'from-choice-mapping') {
    return Object.values(grant.mapping).includes(selectedMoveId);
  }
  if (grant.kind === 'from-group') return true; // skip pool check qui
  return false;
}

function isTargetSetupComplete(
  selectedMove: Move,
  pick: LevelUpMulticlassPickDraft,
  character: Character,
): boolean {
  if (!hasMoveSetup(selectedMove)) return true;
  const moveRef = buildTargetMoveRef(selectedMove, pick);
  return isMoveSetupComplete(selectedMove, moveRef, character.archetypeId);
}

function areBundleSetupsComplete(
  selectedMove: Move,
  pick: LevelUpMulticlassPickDraft,
  character: Character,
): boolean {
  const bundleMovesNeedingSetup = getBundleMovesToApply(selectedMove, character).filter((m) =>
    hasMoveSetup(m),
  );
  for (const bundleMove of bundleMovesNeedingSetup) {
    const setup = pick.bundleSetups?.[bundleMove.id];
    if (setup === undefined) return false;
    const moveRef: MoveRef = { id: bundleMove.id, ...setup };
    if (!isMoveSetupComplete(bundleMove, moveRef, character.archetypeId)) return false;
  }
  return true;
}

function buildTargetMoveRef(move: Move, pick: LevelUpMulticlassPickDraft): MoveRef {
  const moveRef: MoveRef = {
    id: move.id,
    selectedChoices: pick.selectedMoveSetup?.selectedChoices ?? [],
  };
  if (pick.selectedMoveSetup?.selectedChoiceGroups) {
    moveRef.selectedChoiceGroups = pick.selectedMoveSetup.selectedChoiceGroups;
  }
  if (pick.selectedMoveSetup?.narrativeFieldValues) {
    moveRef.narrativeFieldValues = pick.selectedMoveSetup.narrativeFieldValues;
  }
  return moveRef;
}
